import { action, computed, makeObservable, observable, override } from 'mobx'

import { SitePermitStatus } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import {
  ILWFCCategory,
  ILWFCColumn,
  ILWFCRow,
  LWFCRowData,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import DateTimeFormat from '~/client/src/shared/enums/DateTimeFormat'
import LogisticsGroupingOption from '~/client/src/shared/enums/LogisticsGroupingOption'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { ChartDay } from '~/client/src/shared/models/ChartDay'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import SitePermitFollowingsStore from '~/client/src/shared/stores/domain/SitePermitFollowings.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore, {
  HOURS_IN_DAY,
  MILLISECONDS_IN_HOUR,
  MINUTES_IN_HOUR,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import { LogisticsDataKeys } from '~/client/src/shared/types/LogisticsDataKeys'
import { isLogisticWorkflow } from '~/client/src/shared/utils/logisticsHelper'
import {
  EMPTY_STRING,
  NO_SPECIFIED,
  NO_VALUE,
} from '~/client/src/shared/utils/usefulStrings'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import LogisticsStore from '../Logistics/Logistics.store'
import LogisticsListStore from '../LogisticsList/LogisticsList.store'

const unassignedValues = [UNASSIGNED, NO_SPECIFIED, EMPTY_STRING, NO_VALUE]
export const LOGISTICS_GANTT_TIMELINE_WIDTH = 1100
const UPDATE_INTERVAL_MS = MILLISECONDS_IN_HOUR / 2
const CATEGORY_ROW_HEIGHT = 32

export default class LogisticsGanttStore extends LogisticsListStore {
  @observable private currentTimeMs: number = Date.now()
  private requestAnimId: number = null
  private lastUpdateTime: number = 0

  public constructor(
    logisticsStore: LogisticsStore,
    projectDateStore: ProjectDateStore,
    workflowsStore: WorkflowsStore,
    locationAttributesStore: LocationAttributesStore,
    companiesStore: CompaniesStore,
    projectMembersStore: ProjectMembersStore,
    userProjectsStore: UserProjectsStore,
    projectsStore: ProjectsStore,
    state: DesktopInitialState,
    eventsStore: DesktopEventStore,
    workflowFollowingsStore?: SitePermitFollowingsStore,
    workflowTypesStore?: WorkflowTypesStore,
    workflowCategoriesStore?: WorkflowCategoriesStore,
    sharedSitePermitStore?: SharedSitePermitStore,
  ) {
    super(
      logisticsStore,
      projectDateStore,
      workflowsStore,
      locationAttributesStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      projectsStore,
      state,
      eventsStore,
      workflowFollowingsStore,
      workflowTypesStore,
      workflowCategoriesStore,
      sharedSitePermitStore,
    )

    makeObservable(this)
  }

  @override
  public columnsWidthState = new Map<string, number>([
    [LogisticsDataKeys.CHECKBOX, 40],
    [LogisticsDataKeys.LOGISTIC_ID, 250],
    [LogisticsDataKeys.LOGISTIC_NAME, 250],
    [LogisticsDataKeys.LOGISTIC_TYPE, 200],
    [LogisticsDataKeys.LOGISTIC_CATEGORY, 200],
    [LogisticsDataKeys.LOGISTIC_STATUS, 190],
    [LogisticsDataKeys.LOGISTIC_LOCATION, 300],
    [LogisticsDataKeys.LOGISTIC_EQUIPMENT, 300],
    [LogisticsDataKeys.LOGISTIC_COMPANY, 230],
    [LogisticsDataKeys.LOGISTIC_START_DATE, 220],
    [LogisticsDataKeys.LOGISTIC_END_DATE, 220],
    [LogisticsDataKeys.LOGISTIC_RESPONSIBLE, 300],
    [LogisticsDataKeys.LOGISTIC_ATTACHMENT, 200],
    [LogisticsDataKeys.LOGISTICS_TIMELINE, LOGISTICS_GANTT_TIMELINE_WIDTH],
  ])

  @override
  public get defaultColumns(): ILWFCColumn[] {
    const columns: ILWFCColumn[] = [
      {
        dataKey: LogisticsDataKeys.CHECKBOX,
      },
      {
        label: Localization.translator.id_short,
        dataKey: LogisticsDataKeys.LOGISTIC_ID,
        flexGrow: 1,
        isHidden: false,
        isFixed: true,
      },
      {
        label: Localization.translator.name,
        dataKey: LogisticsDataKeys.LOGISTIC_NAME,
        isHidden: false,
        isFixed: true,
      },
      {
        label: Localization.translator.startDate,
        dataKey: LogisticsDataKeys.LOGISTIC_START_DATE,
        isHidden: false,
      },
      {
        label: Localization.translator.finishDate,
        dataKey: LogisticsDataKeys.LOGISTIC_END_DATE,
        isHidden: false,
      },
      {
        label: Localization.translator.type,
        dataKey: LogisticsDataKeys.LOGISTIC_TYPE,
        isHidden: true,
      },
      {
        label: Localization.translator.category,
        dataKey: LogisticsDataKeys.LOGISTIC_CATEGORY,
        isHidden: true,
      },
      {
        label: Localization.translator.status,
        dataKey: LogisticsDataKeys.LOGISTIC_STATUS,
        isHidden: true,
      },
      {
        label: Localization.translator.location,
        dataKey: LogisticsDataKeys.LOGISTIC_LOCATION,
        isHidden: true,
      },
      {
        label: Localization.translator.equipment,
        dataKey: LogisticsDataKeys.LOGISTIC_EQUIPMENT,
        isHidden: true,
      },
      {
        label: Localization.translator.company,
        dataKey: LogisticsDataKeys.LOGISTIC_COMPANY,
        isHidden: true,
      },
      {
        label: Localization.translator.responsibleUser,
        dataKey: LogisticsDataKeys.LOGISTIC_RESPONSIBLE,
        isHidden: true,
      },
      {
        label: Localization.translator.file,
        dataKey: LogisticsDataKeys.LOGISTIC_ATTACHMENT,
        isHidden: true,
      },
      {
        dataKey: LogisticsDataKeys.LOGISTICS_TIMELINE,
        flexGrow: 1,
        isHidden: false,
      },
    ]
    return columns.map(column => ({
      ...column,
      width: this.columnsWidthState.get(column.dataKey),
      isFixed:
        column.dataKey === LogisticsDataKeys.CHECKBOX || !!column.isFixed,
      isHidden: !!column.isHidden,
    }))
  }

  protected get configFieldName(): 'listColumnsConfig' | 'ganttColumnsConfig' {
    return 'ganttColumnsConfig'
  }

  @override
  public get columns(): ILWFCColumn[] {
    const columnsConfig =
      this.workflowsConfiguration?.[this.configFieldName] || []

    if (!columnsConfig?.length) {
      return this.defaultColumns
    }

    const mappedConfigCols = columnsConfig.reduce((list, columnSorted) => {
      const column = this.defaultColumns.find(
        _ => _.dataKey == columnSorted.columnId,
      )
      if (column) {
        list.push({
          ...column,
          width: this.columnsWidthState.get(column.dataKey),
          isHidden:
            typeof columnSorted.isHidden === 'boolean'
              ? columnSorted.isHidden
              : !!column.isHidden,
          isFixed:
            typeof columnSorted.isFixed === 'boolean'
              ? columnSorted.isFixed
              : !!column.isFixed,
        })
      }
      return list
    }, [] as ILWFCColumn[])

    const absentColumns = this.defaultColumns.filter(
      col => !mappedConfigCols.some(config => config.dataKey === col.dataKey),
    )
    absentColumns.forEach(col => mappedConfigCols.push({ ...col }))

    return mappedConfigCols
  }

  @computed
  public get timelineColumnDays(): ChartDay[] {
    const { startDate } = this.logisticsStore
    const { addDays, startOfDay } = this.projectDateStore

    return Array(this.logisticsStore.pickerModeInDays)
      .fill(0)
      .map((_, i) => {
        const currentDay = addDays(startOfDay(startDate), i)

        return new ChartDay(currentDay, this.projectDateStore)
      })
  }

  public get isOneDayView(): boolean {
    return this.logisticsStore.isOneDayCalendarModeActive
  }

  @computed
  public get timelineColumnHours(): number[] {
    if (!this.isOneDayView) {
      return []
    }

    return Array.from({ length: 24 }, (_, i) => i)
  }

  public get currentDate(): Date {
    return this.logisticsStore.startDate
  }

  public formatHourLabel = (hour: number): string => {
    const { activeProject } = this.state
    const isUSFormat =
      !activeProject?.dateTimeFormat ||
      activeProject.dateTimeFormat === DateTimeFormat.US

    if (!isUSFormat) {
      return `${hour}:00`
    }

    if (hour === 0) return '12:00 AM'
    if (hour < 12) return `${hour}:00 AM`

    return hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`
  }

  public isNonWorkingHour = (hour: number): boolean => {
    if (!this.isOneDayView || !this.currentDate) {
      return false
    }

    // The spans of the date on screen, matching the calendar's own shading: a
    // pair of hours cannot express a window running past midnight, and the
    // project-wide window would shade every weekday alike (STX1-983).
    const hourStart = hour * MINUTES_IN_HOUR
    const hourEnd = hourStart + MINUTES_IN_HOUR

    return !this.projectDateStore
      .getWorkingMinuteSpansForDate(this.currentDate)
      .some(
        ([spanStart, spanEnd]) => spanStart < hourEnd && spanEnd > hourStart,
      )
  }

  @computed
  public get currentTimePosition(): { hours: number; pixels: number } {
    if (!this.isOneDayView || !this.currentDate) {
      return null
    }

    const { startOfDay, endOfDay } = this.projectDateStore
    const dayStart = startOfDay(this.currentDate).getTime()
    const dayEnd = endOfDay(this.currentDate).getTime()

    if (this.currentTimeMs < dayStart || this.currentTimeMs > dayEnd) {
      return null
    }

    const msFromDayStart = this.currentTimeMs - dayStart
    const hours = msFromDayStart / MILLISECONDS_IN_HOUR
    const pixels = (hours / HOURS_IN_DAY) * LOGISTICS_GANTT_TIMELINE_WIDTH

    return { hours, pixels }
  }

  @action.bound
  private updateCurrentTime = () => {
    const now = Date.now()

    if (now - this.lastUpdateTime >= UPDATE_INTERVAL_MS) {
      this.currentTimeMs = now
      this.lastUpdateTime = now
    }

    if (this.requestAnimId !== null) {
      this.requestAnimId = requestAnimationFrame(this.updateCurrentTime)
    }
  }

  @action.bound
  public startTimeTracking() {
    if (this.requestAnimId !== null) {
      return
    }

    this.currentTimeMs = Date.now()
    this.lastUpdateTime = Date.now()
    this.requestAnimId = requestAnimationFrame(this.updateCurrentTime)
  }

  @action.bound
  public stopTimeTracking() {
    if (this.requestAnimId !== null) {
      cancelAnimationFrame(this.requestAnimId)
      this.requestAnimId = null
    }
  }

  public startOrStopTimeTrackingIfNeeded() {
    if (this.isOneDayView) {
      this.startTimeTracking()
    } else {
      this.stopTimeTracking()
    }
  }

  @override
  public handleColumnSort(columnKey: string) {
    if (columnKey === LogisticsDataKeys.LOGISTICS_TIMELINE) {
      return
    }
    super.handleColumnSort(columnKey)
  }

  protected toRows(logistics: ILogisticItem[]): any[] {
    return logistics.map(logistic => {
      const data = {
        [LogisticsDataKeys.ID]: logistic.id,
        [LogisticsDataKeys.CHECKBOX]: this.selection.get(logistic.id),
        [LogisticsDataKeys.LOGISTIC_ID]: {
          logisticId: logistic.entityId,
          code: logistic.code,
          isDraft: logistic.status === SitePermitStatus.Draft,
          isShared: logistic.isShared,
        },
        [LogisticsDataKeys.LOGISTIC_NAME]: {
          id: logistic.id,
          name: logistic.shortName || logistic.name,
          workflow: isLogisticWorkflow(logistic)
            ? logistic.original
            : undefined,
        },
        [LogisticsDataKeys.LOGISTIC_TYPE]: {
          type: logistic.type,
          workflow: isLogisticWorkflow(logistic)
            ? logistic.original
            : undefined,
        },
        [LogisticsDataKeys.LOGISTIC_CATEGORY]: {
          categoryName:
            this.workflowCategoriesStore?.getNameById(
              logistic.formCategoryId,
            ) || logistic.formCategoryId,
          categoryId: logistic.formCategoryId,
          workflow: isLogisticWorkflow(logistic)
            ? logistic.original
            : undefined,
        },
        [LogisticsDataKeys.LOGISTIC_STATUS]: {
          status: logistic.status,
          workflowStepLevel: logistic.workflowStepLevel,
          isLate: logistic.isStatusLate,
        },
        [LogisticsDataKeys.LOGISTIC_LOCATION]: {
          isBreadcrumb: false,
          locations: logistic.locations,
        },
        [LogisticsDataKeys.LOGISTIC_EQUIPMENT]: {
          isBreadcrumb: false,
          locations: logistic.equipment,
        },
        [LogisticsDataKeys.LOGISTIC_COMPANY]: logistic.companyIds
          ?.map(id => this.companiesStore.getCompanyById(id))
          .filter(c => c),
        [LogisticsDataKeys.LOGISTIC_START_DATE]: {
          date: logistic?.startDate,
        },
        [LogisticsDataKeys.LOGISTIC_END_DATE]: {
          date: logistic?.endDate,
        },
        [LogisticsDataKeys.LOGISTIC_RESPONSIBLE]:
          this.projectMembersStore.getByIds(logistic.responsibleContactIds),
        [LogisticsDataKeys.LOGISTIC_ATTACHMENT]: {
          attachments: logistic.attachments?.map((a, idx) => ({
            ...a,
            id: a.fileName + idx,
            name: a.fileName,
          })),
        },
        [LogisticsDataKeys.LOGISTICS_TIMELINE]: logistic,
      }

      return { data }
    })
  }

  protected compareRows = (
    { data: aRowData }: ILWFCRow,
    { data: bRowData }: ILWFCRow,
  ): number => {
    const { columnKey, order } = this.sortState

    const v1 = this.getSortingValue(aRowData, columnKey)
    const v2 = this.getSortingValue(bRowData, columnKey)

    if (typeof v1 === 'string' && typeof v2 === 'string') {
      if (unassignedValues.includes(v1)) {
        return 1
      }
      if (unassignedValues.includes(v2)) {
        return -1
      }
      return this.collator.compare(v1, v2) * order
    }

    if (typeof v1 === 'number' && typeof v2 === 'number') {
      return (v1 - v2) * order
    }

    return 0
  }

  protected createCategoryRow(categoryId: string, count: number): ILWFCRow {
    const categoryInstances = this.categoryToInstancesMap[categoryId]

    const category: ILWFCCategory = {
      categoryId,
      shortCategoryLabel: this.getCategoryLabelById(categoryId),
      categoryLabel: `${this.getCategoryLabelById(categoryId)} (${count})`,
      isChecked: categoryInstances.every(
        inst =>
          !this.canSelectInstance(inst) || this.selection.get(inst[this.idKey]),
      ),
    }

    return { category, data: {}, height: CATEGORY_ROW_HEIGHT, level: 0 }
  }

  protected getCategoryLabelById(categoryId: any): string {
    switch (this.groupingKey) {
      case LogisticsGroupingOption.RESPONSIBLE:
        const user = this.projectMembersStore.getById(categoryId)
        return User.getFullNameToDisplay(user, this.userProjectsStore, true)
      default:
        return categoryId
    }
  }

  protected getSortingValue(rowData: LWFCRowData, columnKey: string) {
    switch (columnKey) {
      case LogisticsDataKeys.LOGISTIC_ID:
        return (
          rowData[columnKey]?.code ||
          rowData[columnKey]?.logisticId ||
          EMPTY_STRING
        )
      case LogisticsDataKeys.LOGISTIC_NAME:
        return rowData[columnKey]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_TYPE:
        return rowData[columnKey] || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_CATEGORY:
        return rowData[columnKey] || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_STATUS:
        return rowData[columnKey]?.status || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_LOCATION:
        const locations = rowData[columnKey]?.locations
        return locations?.length > 1
          ? locations.length.toString()
          : locations?.[0]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_EQUIPMENT:
        const equipment = rowData[columnKey]?.locations
        return equipment?.length > 1
          ? equipment.length.toString()
          : equipment?.[0]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_COMPANY:
        const companiesData = rowData[columnKey]
        return companiesData?.length > 1
          ? companiesData.length.toString()
          : companiesData?.[0]?.name || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_START_DATE:
      case LogisticsDataKeys.LOGISTIC_END_DATE:
        return rowData[columnKey]?.date || 0
      case LogisticsDataKeys.LOGISTIC_RESPONSIBLE:
        const usersData = rowData[columnKey]
        return usersData?.length > 1
          ? usersData.length.toString()
          : usersData?.[0]?.fullName || EMPTY_STRING
      case LogisticsDataKeys.LOGISTIC_ATTACHMENT:
        const attachments = rowData[columnKey]?.attachments
        return attachments?.length > 1
          ? attachments.length.toString()
          : attachments?.[0]?.fileName || EMPTY_STRING
      default:
        const value = rowData[columnKey]
        return value === NO_SPECIFIED ? EMPTY_STRING : value
    }
  }
}
